import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { motion } from "framer-motion";
import { ArrowUpRight } from "lucide-react";
import { PageShell } from "../components/PageShell";
import { MaskLines, Reveal } from "../components/Reveal";
import { formatDate } from "../lib/blog";
import { usePosts, categoriesOf } from "../lib/content";

export const PostCard = ({ p, i = 0 }) => (
  <motion.article
    initial={{ opacity: 0, y: 40 }}
    whileInView={{ opacity: 1, y: 0 }}
    viewport={{ once: true, amount: 0.15 }}
    transition={{ duration: 0.9, delay: (i % 3) * 0.1, ease: [0.22, 1, 0.36, 1] }}
    data-testid={`blog-card-${p.slug}`}
  >
    <Link to={`/blog/${p.slug}`} className="group block">
      <div className="relative overflow-hidden aspect-[4/5] bg-[#ECE5DA] rounded-t-[160px]">
        <img src={p.cover} alt={p.title} style={{ objectPosition: p.pos }} className="absolute inset-0 w-full h-full object-cover transition-transform duration-[1400ms] ease-out group-hover:scale-105" />
        <span className="absolute top-5 left-1/2 -translate-x-1/2 overline text-[0.6rem] bg-[#F6F2EC]/85 backdrop-blur-md text-[#14110F] px-3 py-1.5 rounded-full">{p.category}</span>
      </div>
      <p className="mt-6 overline text-[0.62rem] text-[#6b625a]">{formatDate(p.date)} · {p.read} min</p>
      <h3 className="mt-3 font-serif-d font-light text-2xl sm:text-[1.9rem] leading-[1.1] text-[#14110F] transition-colors duration-500 group-hover:text-[#8A6A3A]">{p.title}</h3>
      <p className="mt-3 text-sm text-[#4f4841] font-light leading-relaxed line-clamp-2">{p.excerpt}</p>
      <span className="mt-5 inline-flex items-center gap-2 text-[0.7rem] tracking-[0.2em] uppercase text-[#14110F]">
        Leggi <ArrowUpRight className="w-3.5 h-3.5 transition-transform duration-500 group-hover:rotate-45" />
      </span>
    </Link>
  </motion.article>
);

const Featured = ({ p }) => (
  <Link to={`/blog/${p.slug}`} data-testid="blog-featured" className="group grid lg:grid-cols-12 gap-8 lg:gap-12 items-center">
    <div className="lg:col-span-7 relative overflow-hidden aspect-[4/3] bg-[#14110F]">
      <motion.img
        initial={{ scale: 1.15 }}
        animate={{ scale: 1 }}
        transition={{ duration: 1.8, ease: [0.22, 1, 0.36, 1] }}
        src={p.cover}
        alt={p.title}
        style={{ objectPosition: p.pos }}
        className="absolute inset-0 w-full h-full object-cover transition-opacity duration-700 group-hover:opacity-90"
      />
    </div>
    <div className="lg:col-span-5">
      <p className="overline text-[0.62rem] text-[#8A6A3A]">In evidenza · {p.category}</p>
      <h2 className="mt-5 font-serif-d font-light text-4xl sm:text-5xl leading-[1.02] text-[#14110F] transition-colors duration-500 group-hover:text-[#8A6A3A]">{p.title}</h2>
      <p className="mt-6 text-[#4f4841] font-light leading-relaxed">{p.excerpt}</p>
      <p className="mt-6 overline text-[0.62rem] text-[#6b625a]">{formatDate(p.date)} · {p.read} min di lettura</p>
      <span className="mt-8 inline-flex items-center gap-3 rounded-full bg-[#14110F] text-[#F6F2EC] px-7 py-3.5 text-[0.72rem] tracking-[0.2em] uppercase transition-colors duration-500 group-hover:bg-[#8A6A3A]">
        Leggi l'articolo <ArrowUpRight className="w-4 h-4" />
      </span>
    </div>
  </Link>
);

export default function Blog() {
  const [cat, setCat] = useState("Tutti");
  useEffect(() => { window.scrollTo(0, 0); document.title = "Journal · Gioia Centro Estetico"; }, []);
  const posts = usePosts();
  const [first, ...rest] = posts;
  const list = cat === "Tutti" ? rest : posts.filter((p) => p.category === cat);

  return (
    <PageShell>
      <div className="max-w-[1440px] mx-auto" data-testid="blog-page">
        <div className="grid lg:grid-cols-12 gap-8 items-end">
          <div className="lg:col-span-8">
            <p className="overline text-[#8A6A3A]">Il Journal di Gioia</p>
            <h1 className="mt-6 font-serif-d font-light text-[3.2rem] sm:text-7xl lg:text-[7rem] leading-[0.92] text-[#14110F]" data-testid="blog-title">
              <MaskLines lines={["Consigli di bellezza,"]} delay={0.1} />
              <MaskLines lines={["con metodo."]} delay={0.25} lineClass="italic text-[#8A6A3A]" />
            </h1>
          </div>
          <Reveal className="lg:col-span-4">
            <p className="text-[#4f4841] font-light leading-relaxed">Guide, rituali e risposte alle domande che ci fate più spesso in cabina. Scritti da Gioia e dal suo team.</p>
          </Reveal>
        </div>

        <div className="mt-16 sm:mt-24"><Featured p={first} /></div>

        <div className="mt-24 sm:mt-32 flex flex-wrap gap-3 border-t border-[#ddd4c7] pt-10" data-testid="blog-filters">
          {categoriesOf(posts).map((c) => (
            <button
              key={c}
              data-testid={`blog-filter-${c.toLowerCase()}`}
              onClick={() => setCat(c)}
              className={`rounded-full px-5 py-2 text-sm border transition-colors duration-300 ${cat === c ? "bg-[#14110F] text-[#F6F2EC] border-[#14110F]" : "border-[#ddd4c7] text-[#14110F] hover:border-[#8A6A3A]"}`}
            >
              {c}
            </button>
          ))}
        </div>

        <div className="mt-14 grid sm:grid-cols-2 lg:grid-cols-3 gap-x-8 gap-y-16">
          {list.map((p, i) => <PostCard key={p.slug} p={p} i={i} />)}
        </div>
      </div>
    </PageShell>
  );
}
